import type { ReactNode, JSX } from 'react'
import { useEffect, useState } from 'react'

import { clsx } from 'clsx'

import styles from './ProgressRing.module.css'

const SIZE = 52
const STROKE_WIDTH = 3
const RADIUS = (SIZE - STROKE_WIDTH) / 2
const CIRCUMFERENCE = 2 * Math.PI * RADIUS
const CENTER = SIZE / 2

type ProgressRingProps = {
  /** Portion of the ring to fill, from 0 (empty) to 1 (full). Clamped. */
  fill: number
  children?: ReactNode
  className?: string
  'data-testid'?: string
}

/**
 * Circular progress ring for the home scorecard tiles: a full circular track
 * with a colored arc filled proportionally on top. Popcorn has no
 * circular-progress component (only a linear `Progress` bar), so this lives
 * locally in Home.
 *
 * The arc starts at 12 o'clock and sweeps clockwise. Track and arc colors are
 * themed per state via the `--ring-track-color` / `--ring-arc-color` custom
 * properties.
 */
export function ProgressRing({
  fill,
  children,
  className,
  'data-testid': dataTestId,
}: ProgressRingProps): JSX.Element {
  const target = Math.min(1, Math.max(0, fill))

  // Animate the arc from empty to its target on mount (and on value changes)
  // by starting at 0 and transitioning stroke-dashoffset via CSS.
  const [displayFill, setDisplayFill] = useState(0)
  useEffect(() => {
    setDisplayFill(target)
  }, [target])

  const dashOffset = CIRCUMFERENCE * (1 - displayFill)

  return (
    <span className={clsx(styles.ring, className)} data-testid={dataTestId}>
      <svg
        className={styles.svg}
        viewBox={`0 0 ${SIZE} ${SIZE}`}
        aria-hidden="true"
        data-testid="progress-ring-svg"
      >
        <circle
          className={styles.track}
          cx={CENTER}
          cy={CENTER}
          r={RADIUS}
          strokeWidth={STROKE_WIDTH}
          fill="none"
          data-testid="progress-ring-track"
        />
        {target > 0 && (
          <circle
            className={styles.arc}
            cx={CENTER}
            cy={CENTER}
            r={RADIUS}
            strokeWidth={STROKE_WIDTH}
            fill="none"
            strokeDasharray={CIRCUMFERENCE}
            strokeDashoffset={dashOffset}
            strokeLinecap="round"
            transform={`rotate(-90 ${CENTER} ${CENTER})`}
            data-testid="progress-ring-arc"
          />
        )}
      </svg>
      <span className={styles.center}>{children}</span>
    </span>
  )
}
